'use client';

/**
 * MCP Reimbursement Contracts
 *
 * Shows, per Managed Care Plan (MCP), the program reimbursement "contract"
 * (funding terms) tied to its billing-rule rate schedule and actual
 * reimbursement (claims billed/paid + ROI actuals).
 *
 * Data: GET /api/cbo-reimbursement/mcp-contracts (org-scoped), assembled from
 * program_roi_configurations + sponsoring MCP + program_billing_activities +
 * program_roi_actuals + claims.
 */

import { useTabbedChrome } from '@/components/weavein';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  DollarSign,
  TrendingUp,
  Building2,
  Receipt,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Loader2,
  AlertCircle,
  ListChecks,
} from 'lucide-react';
import {
  serviceReferralsAPI,
  McpReimbursementContract,
  McpContractsReconciliation,
} from '@/services/api/serviceReferrals';

const usd = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n || 0);

const usd2 = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

export default function ReimbursementDashboard() {
  const tabbed = useTabbedChrome();
  const [contracts, setContracts] = useState<McpReimbursementContract[]>([]);
  const [reconciliation, setReconciliation] = useState<McpContractsReconciliation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await serviceReferralsAPI.getMcpReimbursementContracts();
      setContracts(data.contracts || []);
      setReconciliation(data.reconciliation || null);
    } catch (err) {
      console.error('Failed to load reimbursement data:', err);
      setError('Failed to load reimbursement data');
    } finally {
      setLoading(false);
    }
  };

  // Aggregate stats
  const totalBudget = contracts.reduce((s, c) => s + (c.terms.total_budget || 0), 0);
  const totalRevenue = contracts.reduce((s, c) => s + (c.actuals.medicaid_revenue || 0), 0);
  const totalSavings = contracts.reduce((s, c) => s + (c.actuals.cost_savings || 0), 0);
  const totalPaid = contracts.reduce((s, c) => s + (c.claims.paid || 0), 0);
  const avgRoi =
    contracts.length > 0
      ? contracts.reduce((s, c) => s + (c.actuals.roi_percentage || 0), 0) / contracts.length
      : 0;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          {!tabbed && (
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Reimbursement — MCP Contracts
          </h1>
          )}
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Managed Care Plan reimbursement contracts, their tied-in billing rules, and actuals.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="flex items-center gap-3 py-8 text-red-600 dark:text-red-400">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
            <Button variant="outline" size="sm" onClick={loadData} className="ml-auto">
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : contracts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500 dark:text-gray-400">
            <Building2 className="mx-auto mb-3 h-8 w-8 text-gray-300" />
            No MCP reimbursement contracts configured for this organization.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary tiles */}
          <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              { label: 'Contracted budget', value: usd(totalBudget), icon: DollarSign },
              { label: 'Medicaid revenue', value: usd(totalRevenue), icon: TrendingUp },
              { label: 'Cost savings', value: usd(totalSavings), icon: TrendingUp },
              { label: 'Claims paid', value: usd(totalPaid), icon: Receipt },
            ].map((s) => (
              <Card key={s.label}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <s.icon className="h-3.5 w-3.5" />
                    {s.label}
                  </div>
                  <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">
                    {s.value}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {avgRoi > 0 && (
            <div className="mb-4 text-sm text-gray-500 dark:text-gray-400">
              Average program ROI:{' '}
              <span className="font-semibold text-green-700 dark:text-green-400">
                {avgRoi.toFixed(0)}%
              </span>
            </div>
          )}

          {/* Reconciliation banner — contract claims tie to the Claims page total */}
          {reconciliation && (
            <div
              className={`mb-5 flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 text-sm ${
                reconciliation.reconciled
                  ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300'
                  : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300'
              }`}
            >
              <span className="font-medium">
                {reconciliation.reconciled
                  ? '✓ Reconciled — contract claims tie to the Claims page and member records'
                  : '⚠ Some claims are not attributed to a contract program'}
              </span>
              <span className="tabular-nums">
                Contracts billed {usd(reconciliation.contract_claims_billed)} / paid{' '}
                {usd(reconciliation.contract_claims_paid)} · Org total billed{' '}
                {usd(reconciliation.org_claims_billed)} / paid {usd(reconciliation.org_claims_paid)} (
                {reconciliation.org_claim_count} claims)
              </span>
            </div>
          )}

          {/* Contracts */}
          <div className="space-y-5">
            {contracts.map((c) => {
              const isOpen = expanded === c.contract_number;
              const enrollPct =
                c.terms.enrollment_target > 0
                  ? Math.min(
                      100,
                      Math.round((c.actuals.enrollments / c.terms.enrollment_target) * 100)
                    )
                  : 0;
              return (
                <Card key={c.contract_number}>
                  <CardHeader className="pb-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-blue-600" />
                          <CardTitle className="text-base">{c.mcp_name}</CardTitle>
                        </div>
                        <div className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                          {c.program_name}
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="secondary">{c.contract_number}</Badge>
                        {c.actuals.roi_percentage > 0 && (
                          <div className="mt-1 text-sm font-semibold text-green-700 dark:text-green-400">
                            {c.actuals.roi_percentage.toFixed(0)}% ROI
                          </div>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {/* Contract terms */}
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-4">
                      <Stat label="Contract budget" value={usd(c.terms.total_budget)} />
                      <Stat label="PMPM rate" value={usd2(c.terms.avg_pmpm_rate)} />
                      <Stat
                        label="PMPM baseline → target"
                        value={`${usd2(c.terms.baseline_pmpm_cost)} → ${usd2(c.terms.target_pmpm_cost)}`}
                      />
                      <Stat
                        label="ROI horizon"
                        value={c.terms.roi_horizon_months ? `${c.terms.roi_horizon_months} mo` : '—'}
                      />
                    </div>

                    {/* Enrollment vs target */}
                    <div className="mt-4">
                      <div className="mb-1 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>Enrollment (program performance)</span>
                        <span>
                          {c.actuals.enrollments} / {c.terms.enrollment_target} ({enrollPct}%)
                        </span>
                      </div>
                      <Progress value={enrollPct} className="h-2" />
                    </div>

                    {/* Actuals row */}
                    <div className="mt-4 grid grid-cols-3 gap-3 rounded-lg bg-gray-50 p-3 dark:bg-gray-800/40">
                      <Stat label="Medicaid revenue" value={usd(c.actuals.medicaid_revenue)} />
                      <Stat label="Cost savings" value={usd(c.actuals.cost_savings)} />
                      <Stat
                        label="Claims billed / paid"
                        value={`${usd(c.claims.billed)} / ${usd(c.claims.paid)}`}
                      />
                    </div>

                    {/* Enrolled members contributing claims (reimbursement ↔ enrollment) */}
                    {c.enrollment_attribution.length > 0 && (
                      <div className="mt-4">
                        <div className="mb-1 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Claims by enrolled member ({c.active_enrollments} active enrollment
                          {c.active_enrollments === 1 ? '' : 's'})
                        </div>
                        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
                          <table className="w-full text-sm">
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                              {c.enrollment_attribution.map((a) => (
                                <tr key={`${c.contract_number}-${a.enrollment_id ?? a.member_id}`}>
                                  <td className="px-3 py-1.5">
                                    {a.member_name || `Member ${a.member_id}`}
                                    <span className="ml-2 text-xs text-gray-400">
                                      enrollment #{a.enrollment_id ?? '—'}
                                    </span>
                                  </td>
                                  <td className="px-3 py-1.5 text-right text-gray-500">
                                    {a.claim_count} claim{a.claim_count === 1 ? '' : 's'}
                                  </td>
                                  <td className="px-3 py-1.5 text-right tabular-nums">
                                    {usd(a.paid)} / {usd(a.billed)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Billing rules (tied in) */}
                    <button
                      type="button"
                      onClick={() => setExpanded(isOpen ? null : c.contract_number)}
                      className="mt-4 flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                    >
                      <ListChecks className="h-4 w-4" />
                      Billing rules — rate schedule ({c.billing_rules.length})
                      {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    {isOpen && (
                      <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-800/60 dark:text-gray-400">
                            <tr>
                              <th className="px-3 py-2">Activity</th>
                              <th className="px-3 py-2">Code</th>
                              <th className="px-3 py-2">Unit</th>
                              <th className="px-3 py-2 text-right">Rate</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {c.billing_rules.map((r, i) => (
                              <tr key={`${c.contract_number}-${i}`}>
                                <td className="px-3 py-1.5">
                                  {r.activity_name || r.activity_code || '—'}
                                </td>
                                <td className="px-3 py-1.5 font-mono text-xs">
                                  {r.hcpcs_code || r.cpt_code || r.activity_code || '—'}
                                </td>
                                <td className="px-3 py-1.5 text-gray-500">{r.billing_unit || '—'}</td>
                                <td className="px-3 py-1.5 text-right tabular-nums">
                                  {usd2(r.rate_per_unit)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-0.5 font-medium text-gray-900 dark:text-gray-100">{value}</div>
    </div>
  );
}
